import { Component, OnInit } from '@angular/core';
import {
  DictionaryParameterEntity,
  DictionaryParameterService,
} from '../../../app/shared';
import { DictionaryType } from 'src/components/shared/dictionaries-layout';
import { DataTableDataDefinition } from 'src/app/shared/fca-data-table';
import { MatDialog } from '@angular/material/dialog';
import { DictionariesParametersAddNewDialogComponent } from './dictionaries-parameters-add-new-dialog/dictionaries-parameters-add-new-dialog.component';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { ErrorDialogComponent } from 'src/components/shared/error-dialog/error-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { map } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'dictionary-parameters-tab',
  templateUrl: './parameters.component.html',
  styleUrls: ['./parameters.component.scss'],
})
export class DictionaryParametersTab implements OnInit {
  title = 'Dictionary';
  dictionaryType = DictionaryType.Parameters;
  pageSize = 10;

  dataDefinition: DataTableDataDefinition<DictionaryParameterEntity> = [
    {
      name: '',
      allowGroup: true,
      columns: [
        {
          fieldName: 'name',
          caption: 'Parameter',
          valueType: 'text',
          editable: true,
          validator: (value: string | undefined) => {
            if(value === undefined) return null;
            return value.length === 0 ? 'The field cannot be empty' : null;
          },
        },
        {
          fieldName: 'validFrom',
          caption: 'Valid from',
          valueType: 'date',
          editable: true,
        },
        {
          fieldName: 'validTo',
          caption: 'Valid until',
          valueType: 'date',
          editable: true,
        },
        {
          fieldName: 'value',
          caption: 'Value',
          valueType: 'number',
          editable: true,
          validator: (value: number | undefined) => {
            if(value === undefined) return null;
            const maxValueLength = 10;
            const pattern = /^\d{0,10}(\.\d{1,3})?$/;

            if (!value) {
              return 'The field cannot be empty';
            }

            const stringValue = value.toString().replace(',', '.');
            if (stringValue.length > maxValueLength) {
              return `Max length is ${maxValueLength}`;
            }

            if (!pattern.test(stringValue)) {
              return 'Only numbers with optional comma and up to 3 decimal places are allowed';
            }

            return null;
          },
        },
      ],
    },
  ];

  data: DictionaryParameterEntity[] = [];

  constructor(
    private snackBar: MatSnackBar,
    private parameterService: DictionaryParameterService,
    public dialog: MatDialog
  ) {
    this.updateParameter = this.updateParameter.bind(this);
  }

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.parameterService
      .getAll()
      .pipe(
        map((data) =>
          data.map((element) => ({
            id: element.id,
            name: element.name,
            validFrom: this.formatDate(element.validFrom) as unknown as Date,
            validTo: this.formatDate(element.validTo) as unknown as Date,
            value: element.value,
          }))
        )
      )
      .subscribe((respData) => {
        this.data = respData;
      });
  }

  private formatDate(date: Date): string {
    const newDate = new Date(date);
    const formattedDate = `${newDate.getDate().toString().padStart(2, '0')}-${(
      newDate.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}-${newDate.getFullYear()}`;
    return formattedDate;
  }

  distinct(value: string, index: number, array: any[]) {
    return array.indexOf(value) === index;
  }

  addParameter() {
    const parameterNames = this.data
      .sort((x) => x.id)
      .map((x) => x.name)
      .filter(this.distinct);

    let data: {
      parameter: string;
      dateFrom: Date | null;
      dateTo: Date | null;
      parameterNames: string[];
      value: number | null;
    } = {
      parameter: '',
      dateFrom: null,
      dateTo: null,
      parameterNames: parameterNames,
      value: null,
    };

    const dialogRef = this.dialog.open(
      DictionariesParametersAddNewDialogComponent,
      {
        width: '350px',
        panelClass: 'custom-dialog-container',
        scrollStrategy: new NoopScrollStrategy(),
        data: data,
        disableClose: true
      }
    );

    dialogRef.afterClosed().subscribe({
      next: (result: boolean) => {
        if (result !== true) {
          this.snackBar.open('Parameter added.', undefined, {
            duration: 2000,
          });
          this.loadData();
        }
      },
    });
  }

  updateParameter(record: DictionaryParameterEntity) {
    const newRecord = {
      id: record.id,
      name: record.name,
      validFrom: record.validFrom.toString().split('-').reverse().join('-'),
      validTo: record.validTo.toString().split('-').reverse().join('-'),
      value: record.value,
    };
    this.parameterService.update(newRecord).subscribe({
      next: () => {
        this.snackBar.open('Parameter updated.', undefined, { duration: 2000 });
        this.loadData();
      },
      error: (error) => this.handleError(error),
    });
  }

  handleError(error: any) {
    this.dialog
      .open(ErrorDialogComponent, {
        data: { message: error.error.Message },
      })
      .afterClosed()
      .subscribe(() => this.loadData());
  }
}
